import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { supabase } from '@/integrations/supabase/client';
import { PageShell, Section } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  User, 
  Palette, 
  CreditCard, 
  LogOut, 
  Sun, 
  Moon, 
  Monitor,
  Check,
  Loader2,
  Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getPlanDisplayName, getPlanPrice, isExecute } from '@/lib/plans';

export default function Profile() {
  const { user, profile, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Get avatar from Google auth metadata or profile
  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || profile?.avatar_url;
  
  // Generate initials fallback
  const getInitials = () => {
    const name = profile?.display_name || user?.email || '';
    if (profile?.display_name) {
      return profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return name.charAt(0).toUpperCase();
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: displayName || null,
          theme_preference: theme,
        })
        .eq('user_id', user.id);

      if (error) throw error;
      toast.success('Profile updated');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out');
  };

  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ];

  return (
    <PageShell narrow>
      {/* Header */}
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Profile</h1>
        <p className="text-sm text-muted-foreground">
          {user?.email}
          {profile?.display_name && ` · ${profile.display_name}`}
        </p>
      </header>

      {/* Account Section */}
      <Section>
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Account</CardTitle>
            </div>
            <CardDescription>Manage your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar display */}
            <div className="space-y-2">
              <Label>Profile Photo</Label>
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={avatarUrl} alt={profile?.display_name || 'User'} />
                  <AvatarFallback className="bg-primary/10 text-primary text-lg font-medium">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <p className="text-xs text-muted-foreground">
                  Profile photo is synced from your Google account.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
                className="max-w-sm"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handleSaveProfile} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
              <Separator orientation="vertical" className="h-8" />
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Appearance Section */}
      <Section>
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Appearance</CardTitle>
            </div>
            <CardDescription>Customize how YELV looks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Theme</Label>
              <div className="flex gap-2">
                {themeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = theme === option.value;
                  return (
                    <Button
                      key={option.value}
                      variant="outline"
                      size="sm"
                      onClick={() => setTheme(option.value)}
                      className={cn(
                        'flex-1 justify-center gap-2',
                        isSelected && 'border-primary bg-primary/5'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{option.label}</span>
                      {isSelected && <Check className="h-3 w-3 text-primary" />}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Billing Section */}
      <Section>
        <Card id="billing">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Billing</CardTitle>
            </div>
            <CardDescription>Manage your subscription</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                {isExecute(profile?.subscription_tier) && (
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Sparkles className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium">Current Plan</p>
                  <p className="text-lg font-semibold text-foreground">
                    {getPlanDisplayName(profile?.subscription_tier)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {getPlanPrice(profile?.subscription_tier)}
                  </p>
                </div>
              </div>
              <div className={cn(
                "px-2 py-1 text-xs font-medium rounded",
                isExecute(profile?.subscription_tier)
                  ? "bg-primary/10 text-primary"
                  : "bg-secondary/50 text-secondary-foreground"
              )}>
                Active
              </div>
            </div>

            {isExecute(profile?.subscription_tier) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Last payment</p>
                  <p className="font-medium">—</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Next billing</p>
                  <p className="font-medium">—</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              {isExecute(profile?.subscription_tier) ? (
                <Button variant="outline" onClick={() => setShowPaymentModal(true)}>
                  Manage Subscription
                </Button>
              ) : (
                <Button onClick={() => setShowPaymentModal(true)}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Upgrade to Execute
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Payment Coming Soon Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payments Coming Soon</DialogTitle>
            <DialogDescription>
              We're working on subscription management. Premium features will be available soon!
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
